import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { SavedBrandContext } from "contexts/SavedBrand";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { MouseEvent, useContext, useMemo, useState } from "react";
import { SavedBrandCollection } from "schemas/dashboard";
import { HEART_COLOR } from "styles/oneOffColor";

import { useCollection } from "utils/useCollection";

import DeleteCollectionDialog from "../DeleteCollectionDialog";
import styles from "../styles";

interface Props {
  collection: SavedBrandCollection;
  setSelectedCollection: () => void;
  hideDelete?: boolean;
}

const Collection = ({
  collection,
  setSelectedCollection,
  hideDelete = false,
}: Props) => {
  const collectionId = collection.id;
  const collectionName = collection.name;
  const { brandBeingSaved } = useContext(SavedBrandContext);
  const { collections, selectedCollectionId, setSelectedCollectionId } =
    useContext(SavedBrandCollectionsContext);
  const { deleteCollection } = useCollection();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const canDelete =
    !hideDelete &&
    !collection.isDefaultForVa &&
    !brandBeingSaved &&
    collectionId > 0;

  const allCollections = useMemo(
    () => Object.values(collections),
    [collections],
  );

  const totalBrands = collection.unsentCount + collection.sentCount;

  const openConfirm = (e: MouseEvent) => {
    e.stopPropagation();
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (deleteLoading) return;
    setConfirmOpen(false);
  };

  const handleDelete = async (options: {
    removeBrands: boolean;
    moveToCollectionId: number | null;
  }) => {
    setDeleteLoading(true);
    const wasDeleted = await deleteCollection(collectionId, options);
    if (wasDeleted && selectedCollectionId === collectionId) {
      setSelectedCollectionId(null);
    }
    setDeleteLoading(false);
    if (wasDeleted) setConfirmOpen(false);
  };

  if (brandBeingSaved && collectionId === 0) {
    return (
      <Box
        key={`${collectionId}-${collectionName}`}
        sx={styles.collection}
        onClick={setSelectedCollection}
      >
        <Box sx={styles.collectionTitleRow}>
          <Box
            component="i"
            className="fa-regular fa-bookmark"
            sx={styles.collectionTitleIcon}
          />
          <Typography sx={styles.collectionTitle}>{collectionName}</Typography>
          <Box sx={{ ml: "auto", color: HEART_COLOR, fontSize: 12 }}>
            Saved{" "}
            <Box
              component="i"
              className="fa-solid fa-bookmark"
              sx={{ ml: 0.5 }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box key={`${collectionId}-${collectionName}`}>
      <Box sx={styles.collection} onClick={setSelectedCollection}>
        <Box sx={styles.collectionTitleRow}>
          <Box
            component="i"
            className="fa-regular fa-book-bookmark"
            sx={styles.collectionTitleIcon}
          />
          <Typography sx={styles.collectionTitle}>{collectionName}</Typography>
          {collection.isInstagramCollection && (
            <Box
              component="i"
              className="fa-brands fa-instagram"
              sx={{ color: "#E4405F", fontSize: 12, ml: 0.5 }}
            />
          )}
          <Box sx={styles.collectionActions}>
            {canDelete && (
              <Tooltip title="Delete collection">
                <IconButton
                  onClick={openConfirm}
                  sx={styles.collectionIconButton}
                >
                  <Box
                    component="i"
                    className="fa-regular fa-trash"
                    sx={{ fontSize: 11 }}
                  />
                </IconButton>
              </Tooltip>
            )}
            <Box
              component="i"
              className="fa-regular fa-chevron-right"
              sx={styles.collectionChevron}
            />
          </Box>
        </Box>

        <Box sx={styles.badgeRow}>
          <Box sx={styles.badgeGreen}>
            <Box
              component="i"
              className="fa-regular fa-circle-check"
              sx={styles.badgeIcon}
            />
            {totalBrands} brand{totalBrands === 1 ? "" : "s"}
          </Box>
          {collection.unsentCount > 0 && (
            <Box sx={styles.badgeAmber}>
              <Box
                component="i"
                className="fa-regular fa-clock"
                sx={styles.badgeIcon}
              />
              {collection.unsentCount} unsent
            </Box>
          )}
        </Box>

        {collection.sentCount > 0 && (
          <Box sx={styles.metaRow}>
            <Box
              component="i"
              className="fa-regular fa-paper-plane"
              sx={styles.metaIconGreen}
            />
            <Typography sx={styles.metaTextGreen}>
              {collection.sentCount} sent
            </Typography>
          </Box>
        )}
      </Box>
      {canDelete && (
        <DeleteCollectionDialog
          open={confirmOpen}
          collection={collection}
          allCollections={allCollections}
          loading={deleteLoading}
          handleClose={closeConfirm}
          handleConfirm={handleDelete}
        />
      )}
    </Box>
  );
};

export default Collection;
